import { useEffect, useState, type FormEvent } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Info, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  RecentSignInRequiredError,
  useAuth,
  type SecondFactorKind,
  type TotpEnrollment,
} from '@/auth';
import { messages } from '@/i18n';
import { CodeField, ErrorAlert } from './mfaShared';
import { isCompleteCode, secondFactorErrorMessage } from './mfaUtils';
import { ReauthenticateForm } from './ReauthenticateForm';

/** "JBSWY3DPEHPK3PXP" -> "JBSW Y3DP EHPK 3PXP", easier to type from. */
function groupKey(key: string): string {
  return key.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Enroll an authenticator app: a QR code for a desktop, and the key as text
 * plus an otpauth:// link for a phone. Drivers use the dashboard on the same
 * phone that runs the authenticator, so the last two matter as much as the QR.
 */
export function TotpEnrollmentStep({
  onEnrolled,
  onError,
  onCancel,
  reauthPreferKind,
}: {
  onEnrolled: () => void;
  /** Lets a screen react to a specific refusal, e.g. TooManySecondFactorsError. */
  onError?: (error: unknown) => void;
  onCancel?: () => void;
  reauthPreferKind?: SecondFactorKind | undefined;
}) {
  const t = messages.mfa;
  const { startTotpEnrollment, confirmTotpEnrollment } = useAuth();
  const [attempt, setAttempt] = useState(0);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = (err: unknown) => {
    if (err instanceof RecentSignInRequiredError) {
      setNeedsReauth(true);
      return;
    }
    setError(secondFactorErrorMessage(err));
    onError?.(err);
  };

  // A new secret per attempt. After a re-authentication the old secret's
  // session is no longer valid, so the user scans again (see totpRestarted).
  useEffect(() => {
    let cancelled = false;
    startTotpEnrollment()
      .then((next) => {
        if (!cancelled) setEnrollment(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) fail(err);
      });
    return () => {
      cancelled = true;
    };
    // `fail` and `startTotpEnrollment` are recreated each render; the attempt
    // counter is what decides when to start over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (needsReauth) {
    return (
      <ReauthenticateForm
        preferKind={reauthPreferKind}
        onDone={() => {
          setNeedsReauth(false);
          setError(null);
          setCode('');
          setEnrollment(null);
          setAttempt((n) => n + 1);
        }}
        onCancel={() => {
          setNeedsReauth(false);
          onCancel?.();
        }}
      />
    );
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!enrollment || !isCompleteCode(code)) return;
    setError(null);
    setPending(true);
    confirmTotpEnrollment(enrollment, code, t.totpDefaultLabel)
      .then(() => {
        setPending(false);
        onEnrolled();
      })
      .catch((err: unknown) => {
        setPending(false);
        setCode('');
        fail(err);
      });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <div>
        <h2 className="text-base font-semibold">{t.totpStepTitle}</h2>
        <p className="text-muted-foreground mt-1 text-sm">{t.totpStepBody}</p>
      </div>

      {attempt > 0 ? (
        <Alert>
          <Info aria-hidden />
          <AlertDescription>{t.totpRestarted}</AlertDescription>
        </Alert>
      ) : null}

      <ErrorAlert message={error} />

      {enrollment ? (
        <>
          <div className="flex justify-center rounded-md bg-white p-3">
            <QRCodeSVG
              value={enrollment.otpauthUri}
              size={176}
              title={t.totpQrLabel}
              role="img"
              aria-label={t.totpQrLabel}
            />
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground text-xs">{t.totpKeyLabel}</span>
            <code className="bg-secondary/40 rounded-md px-3 py-2 font-mono text-sm break-all select-all">
              {groupKey(enrollment.secretKey)}
            </code>
            <a href={enrollment.otpauthUri} className="text-accent text-xs hover:underline">
              {t.totpOpenInApp}
            </a>
          </div>
        </>
      ) : !error ? (
        <p className="text-muted-foreground text-sm" role="status">
          {t.totpStarting}
        </p>
      ) : null}

      <CodeField value={code} onChange={setCode} disabled={!enrollment || pending} />

      <div className="flex gap-2">
        {onCancel ? (
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            {t.cancel}
          </Button>
        ) : null}
        <Button
          type="submit"
          className="flex-1"
          disabled={!enrollment || pending || !isCompleteCode(code)}
        >
          <ShieldCheck aria-hidden />
          {pending ? t.verifying : t.verify}
        </Button>
      </div>
    </form>
  );
}
